import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ShippingService } from '../../../../../shared/services/shipping.service';

import {
  ShippingStateService,
  ShippingState,
} from '../../../../../shared/services/shipping-state.service';
import { CurrencyService, SupportedCurrency } from '../../../../../core/services/currency.service';
import {
  getCartItemUnitPriceHT,
  getCartTaxEur,
} from '../../../../../shared/cart-pricing.util';

export interface ShippingOption {
  serviceType: string;
  serviceName: string;
  totalPrice: number;
  currency: string;
  deliveryDays?: string | null;
  deliveryDate?: string | null;
}

@Component({
  selector: 'app-order-summary-step',
  standalone: false,
  templateUrl: './order-summary-step.component.html',
  styleUrl: './order-summary-step.component.scss',
})
export class OrderSummaryStepComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formGroup!: FormGroup;
  @Input() step4FormGroup!: FormGroup;
  @Input() cart: any[] = [];
  @Input() voucherDiscountAmount: number = 0;
  @Input() appliedVoucher: any;
  @Input() isProcessingOrder: boolean = false;
  @Input() paymentError: string | null = null;
  @Output() confirmOrder = new EventEmitter<void>();

  // Shipping state
  shippingOptions: ShippingOption[] = [];
  selectedShippingOption: ShippingOption | null = null;
  shippingProvider: 'fedex' | 'local_fallback' | null = null;
  shippingAmount = 0;
  totalAmount = 0;
  isLoadingShipping = false;
  shippingError: string | null = null;

  private countrySubscription?: Subscription;
  private addressModeSubscription?: Subscription;
  private shippingSubscription?: Subscription;
  private currencySubscription?: Subscription;
  private exchangeRateSubscription?: Subscription;

  constructor(
    public readonly shippingService: ShippingService,
    public readonly shippingStateService: ShippingStateService,
    public readonly currencyService: CurrencyService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.shippingSubscription = this.shippingStateService.state$.subscribe((state: ShippingState) => {
      this.isLoadingShipping = state.loading;
      this.shippingProvider = state.provider;
      this.shippingOptions = state.options;
      this.selectedShippingOption = state.selectedOption;
      this.shippingError = state.error;
      this.recalculateTotal();
      this.cdr.detectChanges();
    });

    this.currencySubscription = this.currencyService.currency$.subscribe(() => {
      this.recalculateTotal();
      this.cdr.detectChanges();
    });

    this.exchangeRateSubscription = this.currencyService.rate$.subscribe(() => {
      this.recalculateTotal();
      this.cdr.detectChanges();
    });

    this.recalculateTotal();
    this.fetchShippingRates();
    this.watchCountryChanges();
  }

  get shippingCurrency(): SupportedCurrency {
    return this.currencyService.currentCurrency;
  }

  /** Format an amount already converted to the active currency. */
  formatMoney(amount: number): string {
    return this.currencyService.format(amount, this.shippingCurrency);
  }

  getBuyerTypeLabel(accountType: unknown): string {
    const type = String(accountType ?? '').trim().toLowerCase();
    if (['company', 'business', 'practice', 'professional', '1'].includes(type)) {
      return 'Firma';
    }
    return 'Osoba prywatna';
  }

  get buyerTypeLabel(): string {
    const billingInfo = this.formGroup?.get('step2')?.value;
    return this.getBuyerTypeLabel(
      billingInfo?.accountType ||
        (billingInfo?.companyName ? 'company' : 'individual'),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cart'] && !changes['cart'].firstChange) {
      this.fetchShippingRates(true);
    }
    if (changes['cart'] || changes['voucherDiscountAmount']) {
      this.recalculateTotal();
    }
  }

  ngOnDestroy(): void {
    this.countrySubscription?.unsubscribe();
    this.addressModeSubscription?.unsubscribe();
    this.shippingSubscription?.unsubscribe();
    this.currencySubscription?.unsubscribe();
    this.exchangeRateSubscription?.unsubscribe();
  }

  private watchCountryChanges(): void {
    const step2 = this.formGroup?.get('step2');
    if (!step2) return;

    this.watchSelectedCountry(step2);
    this.addressModeSubscription = step2
      .get('deliverySameAsBilling')
      ?.valueChanges.subscribe(() => this.watchSelectedCountry(step2));
  }

  private watchSelectedCountry(step2: AbstractControl): void {
    this.countrySubscription?.unsubscribe();
    const address = this.getSelectedAddress(step2.getRawValue());
    const countryControl = step2.get(address.countryControl);

    this.countrySubscription = countryControl?.valueChanges.subscribe(() => {
      this.fetchShippingRates(true);
    });
  }

  fetchShippingRates(force = false): void {
    if (!this.cart || this.cart.length === 0) return;
    const currentShipping = this.shippingStateService.currentState;
    if (
      !force &&
      !currentShipping.loading &&
      !currentShipping.error &&
      currentShipping.options.length > 0 &&
      currentShipping.selectedOption
    ) {
      return;
    }

    const step2 = this.formGroup?.get('step2');
    const address = this.getSelectedAddress(step2?.getRawValue());

    this.shippingStateService.fetchRates(
      this.cart,
      address.countryName,
      address.postalCode,
      address.city,
      address.street,
      address.state,
    );
  }

  private getSelectedAddress(value: any) {
    if (value?.deliverySameAsBilling === false) {
      return {
        countryName: value.deliveryCountry || null,
        postalCode: value.deliveryPostalCode || undefined,
        city: value.deliveryCity || undefined,
        street: value.deliveryAddress || undefined,
        state: value.deliveryStateProvince || undefined,
        countryControl: 'deliveryCountry',
      };
    }

    return {
      countryName: value?.otherCountry || null,
      postalCode: value?.postalCode || undefined,
      city: value?.city || undefined,
      street: value?.postcodeTown || undefined,
      state: value?.stateProvince || undefined,
      countryControl: 'otherCountry',
    };
  }

  selectShippingOption(option: ShippingOption): void {
    this.selectedShippingOption = option;
    this.recalculateTotal();
    this.shippingStateService.selectOption(option);
    this.cdr.detectChanges();
  }

  getDeliveryBadge(option: ShippingOption | null): { text: string; isFast: boolean } {
    if (!option) return { text: '', isFast: false };

    const isFast =
      (option.serviceType || '').includes('EXPRESS') ||
      (option.serviceType || '').includes('PRIORITY') ||
      (option.serviceType || '').includes('FIRST');

    // 1. If explicit deliveryDate or timestamp exists
    if (option.deliveryDate) {
      try {
        const d = new Date(option.deliveryDate);
        if (!isNaN(d.getTime())) {
          const formatted = new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }).format(d);
          return {
            text: `Est. delivery by ${formatted}`,
            isFast,
          };
        }
      } catch (_) {}
    }

    // 2. If deliveryDays string exists (e.g. "2-4 working days" or "1-2 working days")
    if (option.deliveryDays) {
      const match = option.deliveryDays.match(/\d+/);
      if (match) {
        const days = parseInt(match[0], 10);
        const estDate = new Date();
        let added = 0;
        while (added < days) {
          estDate.setDate(estDate.getDate() + 1);
          const dow = estDate.getDay();
          if (dow !== 0 && dow !== 6) {
            added++;
          }
        }
        const formatted = new Intl.DateTimeFormat('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }).format(estDate);
        return {
          text: `Est. by ${formatted} (${option.deliveryDays})`,
          isFast: days <= 2,
        };
      }
      return { text: option.deliveryDays, isFast };
    }

    return {
      text: isFast ? 'Est. 1-2 business days' : 'Est. 2-4 business days',
      isFast,
    };
  }

  getCountryName(codeOrName: string | null | undefined): string {
    if (!codeOrName) return '';
    const clean = codeOrName.trim();
    if (clean.length === 2) {
      try {
        if (typeof Intl !== 'undefined' && (Intl as any).DisplayNames) {
          const dn = new Intl.DisplayNames(['en'], { type: 'region' });
          const resolved = dn.of(clean.toUpperCase());
          if (resolved) return resolved;
        }
      } catch (_) {}
    }
    return clean;
  }

  private recalculateTotal(): void {
    this.shippingAmount = this.getShipping();
    const subtotal = this.getSubtotal();
    const tax = this.getTax();
    const downloads = this.getDownloads();
    const discount = this.getVoucherDiscount();
    this.totalAmount = Number(
      Math.max(0, subtotal + tax + downloads + this.shippingAmount - discount).toFixed(2),
    );
  }

  getShipping(): number {
    if (!this.cart || this.cart.length === 0) return 0;
    const shipping = this.shippingStateService.currentState;
    const selectedOption = this.selectedShippingOption;
    const rawCost = selectedOption?.totalPrice ?? this.shippingStateService.currentPrice;
    const rawCurrency =
      selectedOption?.currency || shipping.currency;
    return this.currencyService.convertShipping(rawCost, rawCurrency, this.shippingCurrency);
  }

  getOptionPrice(option: ShippingOption): number {
    if (!option) return 0;
    return this.currencyService.convertShipping(
      option.totalPrice,
      option.currency,
      this.shippingCurrency,
    );
  }

  get isFedEx(): boolean {
    return this.shippingProvider === 'fedex';
  }

  get hasShippingError(): boolean {
    return !!this.shippingError || (!this.isLoadingShipping && !this.selectedShippingOption);
  }

  get isShippingAddressComplete(): boolean {
    const step2 = this.formGroup?.get('step2');
    const deliverySameAsBilling =
      step2?.get('deliverySameAsBilling')?.value !== false;
    const addressControls = deliverySameAsBilling
      ? ['otherCountry', 'postcodeTown', 'postalCode', 'city']
      : ['deliveryCountry', 'deliveryAddress', 'deliveryPostalCode', 'deliveryCity'];
    return addressControls.every((key) =>
      String(step2?.get(key)?.value || '').trim().length > 0,
    );
  }

  get shippingStatusMessage(): string | null {
    if (!this.isShippingAddressComplete) return 'Complete the required delivery address to calculate shipping.';
    if (this.isLoadingShipping) return 'Checking delivery rates for this address...';
    if (this.shippingError) return null;
    if (!this.selectedShippingOption) return 'A verified shipping rate is required before payment.';
    return null;
  }

  get isConfirmDisabled(): boolean {
    return this.isProcessingOrder ||
      this.isLoadingShipping ||
      this.hasShippingError;
  }

  onConfirmOrder(): void {
    // Prevent multiple submissions when processing
    if (this.isProcessingOrder) {
      return;
    }

    // Block order submission if shipping rates are unavailable or errored
    const shipping = this.shippingStateService.currentState;
    if (shipping.loading || shipping.error || !shipping.selectedOption) {
      return;
    }

    // Check if terms and conditions are accepted
    if (this.step4FormGroup && this.step4FormGroup.invalid) {
      this.markFormGroupTouched(this.step4FormGroup);
      return;
    }

    // Add a small visual feedback delay to prevent accidental double-clicks
    setTimeout(() => {
      this.confirmOrder.emit();
    }, 150);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getBaseSubtotalEur(): number {
    if (!this.cart || this.cart.length === 0) return 0;

    return this.cart.reduce((total, item) => {
      const discountedPriceHT = this.getDiscountedPriceHT(item);
      const quantity = parseInt(item?.quantity) || 0;
      return total + discountedPriceHT * quantity;
    }, 0);
  }

  getSubtotal(): number {
    return this.currencyService.convertFromEur(this.getBaseSubtotalEur(), this.shippingCurrency);
  }

  getBaseTaxEur(): number {
    return getCartTaxEur(this.cart || []);
  }

  getTax(): number {
    return this.currencyService.convertFromEur(this.getBaseTaxEur(), this.shippingCurrency);
  }

  // Get discounted price without tax in EUR
  getDiscountedPriceHT(item: any): number {
    return getCartItemUnitPriceHT(item);
  }

  getBaseDownloadsEur(): number {
    if (!this.cart || this.cart.length === 0) return 0;

    return this.cart.reduce((total, item) => {
      if (!item?.selectedDownloads || item.selectedDownloads.length === 0) {
        return total;
      }

      const itemDownloadsTotal = item.selectedDownloads.reduce((downloadTotal: number, download: any) => {
        const downloadPrice = parseFloat(download.price || 0);
        return downloadTotal + downloadPrice;
      }, 0);

      return total + itemDownloadsTotal;
    }, 0);
  }

  getDownloads(): number {
    return this.currencyService.convertFromEur(this.getBaseDownloadsEur(), this.shippingCurrency);
  }

  hasDownloads(): boolean {
    if (!this.cart || this.cart.length === 0) return false;

    return this.cart.some(item =>
      item?.selectedDownloads &&
      item.selectedDownloads.length > 0 &&
      item.selectedDownloads.some((download: any) => parseFloat(download.price || 0) > 0)
    );
  }

  getVoucherDiscount(): number {
    return this.currencyService.convertFromEur(
      this.voucherDiscountAmount || 0,
      this.shippingCurrency,
    );
  }

  getTotal(): number {
    return this.totalAmount;
  }

  getTotalQuantity(): number {
    return (this.cart || []).reduce(
      (total, item) => total + (parseInt(item?.quantity, 10) || 0),
      0,
    );
  }

  // Helper method to get unit price (converted to active currency for display)
  getUnitPrice(item: any): number {
    const base = this.getDiscountedPriceHT(item);
    return this.currencyService.convertFromEur(base, this.shippingCurrency);
  }

  // Helper method to get item total (converted to active currency for display)
  getItemTotal(item: any): number {
    const discountedPriceHT = this.getDiscountedPriceHT(item);
    const quantity = parseInt(item?.quantity) || 0;
    return this.currencyService.convertFromEur(
      discountedPriceHT * quantity,
      this.shippingCurrency,
    );
  }

  // Variants display helpers
  hasVariantValues(item: any): boolean {
    return this.getVariantValues(item).length > 0;
  }

  getVariantValues(item: any): string[] {
    if (!item) return [];

    // Prefer explicit combination values if available
    const combination = item?.combination;
    try {
      if (typeof combination === 'string') {
        const parsed = JSON.parse(combination);
        if (Array.isArray(parsed)) {
          return parsed.map((v: any) => String(v)).filter(Boolean);
        }
        if (parsed && Array.isArray(parsed.values)) {
          return parsed.values.map((v: any) => String(v)).filter(Boolean);
        }
      }
    } catch (_) {
      // ignore JSON parse errors
    }

    if (combination && Array.isArray(combination.values)) {
      return combination.values.map((v: any) => String(v)).filter(Boolean);
    }

    if (Array.isArray(combination)) {
      return combination.map((v: any) => String(v)).filter(Boolean);
    }

    // Fall back to variants object { [variantId]: { value, variant: { name } } }
    const variantValues: string[] = [];
    const variantsObject = item?.variants || {};
    Object.keys(variantsObject).forEach((key: string) => {
      const variantEntry = variantsObject[key];
      const valueText = variantEntry?.value || variantEntry?.name || variantEntry?.label;
      if (valueText) variantValues.push(String(valueText));
    });

    // Include selected color if present
    const selectedColor = item?.selectedColor;
    const colorText = selectedColor?.name || selectedColor?.label || selectedColor?.value;
    if (colorText) variantValues.push(String(colorText));

    return variantValues;
  }

  // Color swatch helper (maps common names to hex; falls back to hash)
  getColorHex(colorName: string): string {
    if (!colorName) return '#cccccc';
    const map: { [k: string]: string } = {
      black: '#000000', white: '#ffffff', red: '#ef4444', blue: '#3b82f6', green: '#10b981',
      yellow: '#f59e0b', orange: '#f97316', purple: '#8b5cf6', pink: '#ec4899', gray: '#9ca3af',
      grey: '#9ca3af', brown: '#92400e', navy: '#1e3a8a', beige: '#f5f5dc', khaki: '#bdb76b'
    };
    const key = colorName.toLowerCase();
    if (map[key]) return map[key];
    // Generate color from string
    let hash = 0;
    for (let i = 0; i < colorName.length; i++) {
      hash = colorName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const r = (hash >> 16) & 0xff;
    const g = (hash >> 8) & 0xff;
    const b = hash & 0xff;
    return `#${(r & 0xff).toString(16).padStart(2, '0')}${(g & 0xff).toString(16).padStart(2, '0')}${(b & 0xff).toString(16).padStart(2, '0')}`;
  }
}
